import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLang } from "@/context/LanguageContext";
import { LogIn, ArrowRight } from "lucide-react";

export default function CustomerLogin() {
  const { customer, login } = useCustomerAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/account";

  if (customer) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success(t("auth_login_success"));
      nav(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth_login_error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-xd-light flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyanHover">{t("auth_kicker_login")}</div>
            <h1 className="font-display text-3xl font-semibold mt-2">{t("auth_login_title")}</h1>
            <p className="text-sm text-xd-muted mt-2">{t("auth_login_desc")}</p>
          </div>
          <form onSubmit={submit} className="bg-white border border-xd-border rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-xd-muted font-mono-tech">{t("auth_field_email")}</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="customer-login-email" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-xd-muted font-mono-tech">{t("auth_field_password")}</label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="customer-login-password" className="mt-1" />
            </div>
            <button type="submit" disabled={loading} data-testid="customer-login-submit" className="w-full btn-cta rounded-lg py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <LogIn className="w-4 h-4" /> {loading ? t("auth_login_loading") : t("auth_login_cta")}
            </button>
            <div className="text-center text-sm text-xd-muted pt-2 border-t border-xd-border">
              {t("auth_no_account")}{" "}
              <Link to="/registrati" data-testid="link-register" className="text-xd-cyanHover font-semibold hover:underline inline-flex items-center gap-1">
                {t("auth_register_link")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
